/* Le rotte, già al loro percorso definitivo.
 *
 * Le schermate sono ancora segnaposto ma gli URL no: un indirizzo che cambia
 * dopo è un segnalibro rotto e un link condiviso che non funziona più.
 *
 * Tutto sta dentro il cancello tranne /accesso: il backend protegge ogni
 * endpoint tranne il login, quindi montare una schermata senza sessione
 * significherebbe mostrarne una vuota che poi si riempie di 401.
 */

import { Navigate, createBrowserRouter } from 'react-router-dom';
import { Guscio } from '../componenti/Guscio';
import { RichiedeAccesso } from '../auth/RichiedeAccesso';
import { Accesso } from '../pagine/Accesso';
import {
  Cassaforte,
  ConfrontoBacktest,
  Dashboard,
  DettaglioBacktest,
  DettaglioTradebot,
  Gruppi,
  Impostazioni,
  ListaTradebot,
  NonTrovata,
  NuovoBacktest,
  NuovoTradebot,
  Preferiti,
  TuttiIBacktest,
  Wallet,
} from '../pagine';

/**
 * Gli indirizzi del frontend precedente, mandati dove la cosa vive adesso.
 *
 * Senza questa tabella chi aveva un segnalibro -- o semplicemente la scheda ancora aperta --
 * finiva su una pagina «non trovata» subito dopo essere entrato, che e' il primo incontro
 * peggiore possibile con una interfaccia nuova. Il caso piu' frequente e' `/login`, che il
 * browser ripropone da solo, e `/backtest-search`, che era la schermata piu' usata.
 *
 * `/backtest` NON compare qui, ed e' voluto: esiste in entrambe le versioni, ma prima era il
 * modulo per lanciare un backtest e adesso e' l'elenco. Il vecchio indirizzo continua a
 * funzionare e porta a una schermata sensata, solo diversa da quella di prima.
 */
const VECCHI_INDIRIZZI: Array<[string, string]> = [
  ['/login', '/accesso'],
  ['/backtest-search', '/backtest'],
  ['/backtest-singles', '/backtest'],
  ['/backtest-group', '/gruppi'],
  ['/backtest-favorites', '/preferiti'],
  ['/tradebots', '/tradebot'],
  ['/wallets', '/wallet'],
  ['/personal-wallets', '/cassaforte'],
  ['/wallet-create', '/wallet'],
  ['/accounts', '/impostazioni/account'],
  ['/account-create', '/impostazioni/account'],
  ['/account-balances', '/impostazioni/account'],
  ['/exchanges', '/impostazioni/account'],
  ['/stored-data', '/impostazioni/storico'],
];

export const rotte = createBrowserRouter([
  { path: '/accesso', element: <Accesso /> },
  ...VECCHI_INDIRIZZI.map(([vecchio, nuovo]) => ({
    path: vecchio,
    element: <Navigate to={nuovo} replace />,
  })),
  // Le forme con parametro non si possono mappare in tabella: il `:id` va riportato a mano.
  { path: '/wallet-edit/:id', element: <Navigate to="/wallet" replace /> },
  { path: '/account-edit/:id', element: <Navigate to="/impostazioni/account" replace /> },
  { path: '/exchange-edit/:id', element: <Navigate to="/impostazioni/account" replace /> },
  { path: '/exchange-create', element: <Navigate to="/impostazioni/account" replace /> },
  {
    path: '/',
    element: (
      <RichiedeAccesso>
        <Guscio />
      </RichiedeAccesso>
    ),
    children: [
      { index: true, element: <Dashboard /> },

      { path: 'backtest', element: <TuttiIBacktest /> },
      /* `nuovo` sta PRIMA di `:id` e non è indifferente: dopo, il router
         leggerebbe «nuovo» come un identificativo e la schermata cercherebbe
         il backtest numero NaN. */
      { path: 'backtest/nuovo', element: <NuovoBacktest /> },
      /* Anche `confronto` sta PRIMA di `:id`, e per la stessa ragione: dopo,
         il router leggerebbe «confronto» come un identificativo e la schermata
         chiederebbe al server il backtest numero NaN. La selezione viaggia in
         query string, non nel percorso, perche' e' una lista di lunghezza
         variabile e un segmento con quattro id dentro non e' un indirizzo. */
      { path: 'backtest/confronto', element: <ConfrontoBacktest /> },
      { path: 'backtest/:id', element: <DettaglioBacktest /> },

      { path: 'gruppi', element: <Gruppi /> },
      /* Il dettaglio di un gruppo è la ricerca filtrata su quel gruppo: stessa
         schermata, stesso ordinamento indipendente. */
      { path: 'gruppi/:id', element: <Gruppi /> },

      { path: 'preferiti', element: <Preferiti /> },

      { path: 'tradebot', element: <ListaTradebot /> },
      /* `nuovo` PRIMA di `:id`, e non e' un dettaglio: `/tradebot/nuovo`
         combacia con entrambe le forme, e un segmento statico dichiarato dopo
         un parametro si finisce per leggere come un identificativo — con la
         schermata che chiede al server il bot numero «nuovo». */
      { path: 'tradebot/nuovo', element: <NuovoTradebot /> },
      { path: 'tradebot/:id', element: <DettaglioTradebot /> },

      { path: 'wallet', element: <Wallet /> },
      { path: 'wallet/:id', element: <Wallet /> },

      /* La cassaforte e' l'altra tabella, `personal_wallet`: sta su un
         percorso suo e non sotto `/wallet/...` proprio per non farle
         sembrare la stessa cosa. */
      { path: 'cassaforte', element: <Cassaforte /> },

      { path: 'impostazioni', element: <Impostazioni /> },
      /* Le sezioni minori del prototipo (account & exchange, dati storici)
         vivono dentro Impostazioni, con la sezione nell'URL. */
      { path: 'impostazioni/:sezione', element: <Impostazioni /> },

      { path: '404', element: <NonTrovata /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
]);
